import { displayLabel } from "../../utils/displayLabel";
import React from "react";
import { unwrap } from "./circleActivityUtils";

const genreColorMap = {
  Fantasy: "#1E7F5C",
  Romantasy: "#B832C9",
  Romance: "#FF1D8D",
  Western: "#704214",
  Horror: "#990000",
  "Sci-fi": "#1F4ED8",
  Mystery: "#111111",
  Thriller: "#111111",
  Crime: "#111111",
  "Non-Fiction": "#4A5560",
  Historical: "#E36A2E",
  Children: "#F4C430",
  Christian: "#E6B7C6",
  Literary: "#7A5C8E",
};

const FALLBACK_COLOR = "#541675";

const NOTIFICATION_TYPES = {
  book_release: "book_release",
  author_update: "author_update",
  author_news: "author_update",
  genre_update: "genre_update",
  genre_news: "genre_update",
};

const normalizeType = (rawType) =>
  NOTIFICATION_TYPES[rawType] || rawType || "unknown";

const deriveCategory = ({ category, genre, type }) => {
  if (category) return category;
  if (genre) return genre;

  if (type === "book_release") return "Books";
  if (type === "author_update") return "Authors";
  if (type === "genre_update") return "Genres";

  return "General";
};

const ensureNotificationMeta = (item = {}) => {
  const type = normalizeType(unwrap(item.type));
  const title = displayLabel(unwrap(item.title)) || "Untitled update";
  const authorName = displayLabel(unwrap(item.authorName || item.penName)) || "Your circle";
  const genre = displayLabel(unwrap(item.genre)) || "General";
  const link = unwrap(item.link) || "#";
  const createdAt = unwrap(item.createdAt || item.SK) || "";
  const category = deriveCategory({
    category: displayLabel(unwrap(item.category)),
    genre,
    type,
  });

  return {
    ...item,
    type,
    title,
    authorName,
    genre,
    link,
    createdAt,
    category,
  };
};

const BaseNotificationCard = ({
  item,
  i,
  formatTime,
  label,
  accentColor,
  subtitle,
}) => {
  const rightMeta = subtitle || `${item.authorName} · ${formatTime(item.createdAt)}`;

  return (
    <a
      key={i}
      href={item.link}
      className="group flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white border border-bookish-purple/10 rounded-xl shadow-sm hover:shadow-md transition"
      aria-label={`${label}: ${item.title}`}
    >
      <div className="flex items-center gap-2 sm:gap-4">
        <div
          className="w-4 h-4 rounded-full border-2 transition group-hover:scale-110"
          style={{ borderColor: accentColor }}
        >
          <div
            className="w-full h-full rounded-full opacity-0 group-hover:opacity-100 transition"
            style={{ backgroundColor: accentColor }}
          />
        </div>

        <div className="flex flex-col">
          <div className="text-sm tracking-wide uppercase font-semibold text-bookish-purple">
            {label}
          </div>
          <div className="text-xs text-bookish-purpleAccent/80">
            {displayLabel(item.category)}
          </div>
        </div>
      </div>

      <div className="text-lg font-serif text-bookish-purple">{displayLabel(item.title)}</div>

      <div className="text-sm text-bookish-purpleAccent">{rightMeta}</div>
    </a>
  );
};

const RENDERERS = {
  book_release: (item, i, formatTime) => (
    <BaseNotificationCard
      item={item}
      i={i}
      formatTime={formatTime}
      label="NEW BOOK RELEASE"
      accentColor={genreColorMap[item.genre] || FALLBACK_COLOR}
    />
  ),
  author_update: (item, i, formatTime) => (
    <BaseNotificationCard
      item={item}
      i={i}
      formatTime={formatTime}
      label="AUTHOR UPDATE"
      accentColor="#3D4A90"
      subtitle={`${item.authorName} · ${formatTime(item.createdAt)}`}
    />
  ),
  genre_update: (item, i, formatTime) => (
    <BaseNotificationCard
      item={item}
      i={i}
      formatTime={formatTime}
      label="GENRE UPDATE"
      accentColor={genreColorMap[item.genre] || FALLBACK_COLOR}
      subtitle={`${item.genre} · ${formatTime(item.createdAt)}`}
    />
  ),
};

const fallbackRenderer = (item, i, formatTime) => (
  <BaseNotificationCard
    item={item}
    i={i}
    formatTime={formatTime}
    label="UPDATE"
    accentColor={FALLBACK_COLOR}
  />
);

export const renderFeedNotification = (rawItem, i, formatTime) => {
  const item = ensureNotificationMeta(rawItem);
  const renderer = RENDERERS[item.type] || fallbackRenderer;
  return renderer(item, i, formatTime);
};

export { ensureNotificationMeta };
